import { useCallback, useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/components/auth';
import { websiteUrls } from '@/lib/site-urls';

export interface SubscriptionInfo {
  subscriptionId: string | null;
  subscriptionItemId: string | null;
  status: string;
  planId: string;
  currentPeriodEnd: number | null;
  cancelAt: number | null;
  cancelAtPeriodEnd: boolean;
  pendingPlanId: string | null;
  scheduleId: string | null;
}


export function useSubscription() {
  const { token } = useSupabaseAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const headers = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  const refresh = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(websiteUrls.billing('subscription'), {
        headers: headers(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as SubscriptionInfo;
      setSubscription(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [token, headers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Handle URL params after returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('billing_success') || params.has('billing_canceled')) {
      // Clean the URL without triggering a navigation
      const url = new URL(window.location.href);
      url.searchParams.delete('billing_success');
      url.searchParams.delete('billing_canceled');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      // Refresh subscription data to pick up any changes
      refresh();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when the window regains focus (e.g. returning from Stripe portal tab)
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const checkout = useCallback(
    async (planId: string) => {
      if (!token) return;
      const res = await fetch(websiteUrls.billing('checkout'), {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          plan_id: planId,
          return_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { url: string } | { updated: true };
      if ('updated' in data) {
        // Subscription was updated server-side (plan change), just refresh
        await refresh();
        return;
      }
      // New subscription — redirect to Stripe Checkout
      window.location.href = data.url;
    },
    [token, headers, refresh],
  );

  const previewProration = useCallback(
    async (planId: string): Promise<{ immediateChargeUsd: number; currency: string }> => {
      if (!token) throw new Error('Not authenticated');
      const url = new URL(websiteUrls.billing('proration-preview'));
      url.searchParams.set('plan_id', planId);
      const res = await fetch(url.toString(), { headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    [token, headers],
  );

  const openPortal = useCallback(async () => {
    if (!token) return;
    const res = await fetch(websiteUrls.billing('portal'), {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ return_url: window.location.origin }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    const { url } = (await res.json()) as { url: string };
    window.location.href = url;
  }, [token, headers]);

  const resumeSubscription = useCallback(async () => {
    if (!token) return;
    const res = await fetch(websiteUrls.billing("subscription", "resume"), {
      method: 'POST',
      headers: headers(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    await refresh();
  }, [token, headers, refresh]);

  const cancelSubscription = useCallback(async () => {
    if (!token) return;
    const res = await fetch(websiteUrls.billing("subscription", "cancel"), {
      method: 'POST',
      headers: headers(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
    }
    await refresh();
  }, [token, headers, refresh]);

  const currentPlanId = subscription?.planId ?? 'free';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isCanceling = isActive && (subscription?.cancelAtPeriodEnd || subscription?.cancelAt != null);
  const currentPeriodEnd = subscription?.currentPeriodEnd ?? null;
  const cancellationDate = subscription?.cancelAt ?? currentPeriodEnd;
  const pendingPlanId = subscription?.pendingPlanId ?? null;
  const isDowngrading = pendingPlanId !== null;

  return {
    subscription,
    currentPlanId,
    isActive,
    isCanceling,
    isDowngrading,
    pendingPlanId,
    currentPeriodEnd,
    cancellationDate,
    loading,
    error,
    checkout,
    openPortal,
    cancelSubscription,
    resumeSubscription,
    previewProration,
    refresh,
  };
}
